import { useTranslation } from 'react-i18next';

import type { WebsiteDraft, WebsiteStepProps } from '../types';
import { WEBSITE_MODULES, type WebsiteModule } from '../../api/websites';

// Content is step 3: which teams to feature on the public site, which
// content modules to enable. Both write to the draft only — actual
// PATCHes (teams.public_visibility, website.enabled_modules) happen on
// final submit so the wizard is fully reversible until the user clicks
// Publish.

function ModuleToggle({
  module,
  on,
  onToggle,
}: { module: WebsiteModule; on: boolean; onToggle: () => void }) {
  const { t } = useTranslation('common');
  return (
    <label className={'wizard-module-toggle' + (on ? ' wizard-module-toggle--on' : '')}>
      <input type="checkbox" checked={on} onChange={onToggle} />
      <div className="wizard-module-toggle__body">
        <strong>{t(`website.wizard.module.${module}.label`)}</strong>
        <p>{t(`website.wizard.module.${module}.description`)}</p>
      </div>
    </label>
  );
}

export function Content({ draft, toggleModule, toggleTeam }: WebsiteStepProps) {
  const { t } = useTranslation('common');

  return (
    <div className="wizard-content">
      <section className="wizard-content__teams">
        <h3>{t('website.wizard.content.teamsHeading')}</h3>
        <p className="wizard-content__hint">
          {t('website.wizard.content.teamsHint')}
        </p>
        {draft.teams.length === 0 ? (
          <p className="wizard-content__empty">{t('website.wizard.content.noTeams')}</p>
        ) : (
          <ul className="wizard-content__team-list">
            {draft.teams.map((team) => {
              const on = draft.teamSelection.get(team.id) ?? false;
              return (
                <li key={team.id}>
                  <label className={'wizard-team-toggle' + (on ? ' wizard-team-toggle--on' : '')}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => toggleTeam(team.id, e.target.checked)}
                    />
                    <span className="wizard-team-toggle__name">{team.name}</span>
                    {team.member_count > 0 && (
                      <span className="wizard-team-toggle__count">{team.member_count}</span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="wizard-content__modules">
        <h3>{t('website.wizard.content.modulesHeading')}</h3>
        <p className="wizard-content__hint">{t('website.wizard.content.modulesHint')}</p>
        <div className="wizard-content__modules-grid">
          {WEBSITE_MODULES.map((m) => (
            <ModuleToggle
              key={m}
              module={m}
              on={draft.enabledModules.has(m)}
              onToggle={() => toggleModule(m)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

export function contentIsValid(d: WebsiteDraft): boolean {
  // Allow zero teams + zero modules — both are valid (the site renders
  // a bare hero + footer in that case). The Preview step warns visually
  // if the page would be sparse, but doesn't block.
  void d;
  return true;
}
